import { createApi } from "@reduxjs/toolkit/query/react";
import {
  createAuthBaseQuery,
} from "../../common/utils";
// @ts-ignore
import { HOSTNAME } from "../../constant/host";

// ===== 型定義 =====

export interface ProjectMembership {
  user_id: number;
  position: string;
  budget: number;
  allocated_budget: number;
  current_budget: number;
  total_rewards: number;
  is_invited: boolean;
  can_rate: boolean;
  is_read_only: boolean;
  order: number;
  order_user: number;
  notification: string;
  avatar_url?: string;
  display_name_jp?: string;
  display_name_en?: string;
  title_jp?: string;
  title_en?: string;
  affiliation_jp?: string;
  email?: string;
  allowed_ips: string[];
  /**
   * 認証ユーザーが当該メンバーをフォロー中かどうか。
   * BE で `accounts.services.annotate_relations` 等を使って付与される想定。
   * 未付与のレスポンスでは undefined になるため UI 側はフォールバックとして false 扱い。
   */
  is_following?: boolean;
}

interface MembershipResponse {
  status: string;
  data: ProjectMembership;
}

interface MembershipsListResponse {
  status: string;
  count: number;
  data: ProjectMembership[];
}

interface AllMembershipsResponse {
  status: string;
  count: number;
  current_user_role: string;
  data: ProjectMembership[];
}

interface SortParams {
  sort_by?: "order" | "order_user" | "position" | "created";
  order?: "asc" | "desc";
}

interface AllMembershipsParams extends SortParams {
  position?: string;
}

interface UpdateMembershipRequest {
  projectId: string;
  userId: number;
  position?: string;
  notification?: string;
  can_rate?: boolean;
  is_read_only?: boolean;
  order_user?: number;
  budget?: number;
  allocated_budget?: number;
  allowed_ips?: string[] | null;
}

interface RemoveMembershipRequest {
  projectId: string;
  userId: number;
}

// ===== API定義 =====

export const membershipsApi = createApi({
  reducerPath: "membershipsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Membership", "MembershipsList"],
  endpoints: (builder) => ({
    // 認証ユーザーの指定プロジェクトにおけるメンバーシップ情報を取得
    getProjectMembership: builder.query<MembershipResponse, string>({
      query: (projectId) => ({
        url: `/api/${projectId}/memberships?user=me`,
        method: "GET",
      }),
      providesTags: (_result, _error, projectId) => [
        { type: "Membership", id: projectId },
      ],
    }),

    // 認証ユーザーのアクティブなプロジェクトメンバーシップ一覧
    getActiveProjectMemberships: builder.query<MembershipsListResponse, SortParams>({
      query: ({ sort_by = "order", order = "asc" }) => {
        const queryString = new URLSearchParams({
          status: "active",
          sort_by,
          order,
        }).toString();
        return {
          url: `/api/me/memberships?${queryString}`,
          method: "GET",
        };
      },
      providesTags: ["MembershipsList"],
      keepUnusedDataFor: 0,
    }),

    // 認証ユーザーの完了したプロジェクトメンバーシップ一覧
    getCompletedProjectMemberships: builder.query<MembershipsListResponse, SortParams>({
      query: ({ sort_by = "order", order = "asc" }) => {
        const queryString = new URLSearchParams({
          status: "completed",
          sort_by,
          order,
        }).toString();
        return {
          url: `/api/me/memberships?${queryString}`,
          method: "GET",
        };
      },
      providesTags: ["MembershipsList"],
      keepUnusedDataFor: 0,
    }),

    // プロジェクトのすべてのメンバーシップ一覧
    getAllProjectMemberships: builder.query<AllMembershipsResponse, { projectId: string } & AllMembershipsParams>({
      query: ({ projectId, sort_by = "order_user", order = "asc", position }) => {
        const params: Record<string, string> = {
          sort_by,
          order,
        };
        if (position) {
          params.position = position;
        }
        const queryString = new URLSearchParams(params).toString();
        return {
          url: `/api/${projectId}/memberships?${queryString}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, { projectId }) => [
        { type: "MembershipsList", id: projectId },
      ],
      keepUnusedDataFor: 0,
    }),

    // メンバーシップ更新（ポジション変更、通知設定等）
    updateMembership: builder.mutation<MembershipResponse, UpdateMembershipRequest>({
      query: ({ projectId, userId, ...body }) => ({
        url: `/api/${projectId}/memberships/${userId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "MembershipsList", id: projectId },
        { type: "Membership", id: projectId },
      ],
    }),

    // メンバーシップ削除（プロジェクトから退出）
    removeMembership: builder.mutation<{ status: string }, RemoveMembershipRequest>({
      query: ({ projectId, userId }) => ({
        url: `/api/${projectId}/memberships/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "MembershipsList", id: projectId },
        { type: "Membership", id: projectId },
      ],
    }),
  }),
});

export const {
  useGetProjectMembershipQuery,
  useGetActiveProjectMembershipsQuery,
  useGetCompletedProjectMembershipsQuery,
  useGetAllProjectMembershipsQuery,
  useUpdateMembershipMutation,
  useRemoveMembershipMutation,
} = membershipsApi;
